export class SampleConsensus {
  constructor(model, threshold = Number.MAX_VALUE, random = false) {
    this.sacModel = model;
    this.probability = 0.99;
    this.iterations = 0;
    this.threshold = threshold;
    this.maxIterations = 1000;
    // this.threads = -1;
    this.threads = 1;
    this.rng = random ? Math.random : () => 0.12345; // Simple random generator or seed-based

    // Handle seeding if random flag is set
    if (random) {
      this.seedRNG();
    }
  }

  // Helper to seed the random number generator (you can use a better RNG here)
  seedRNG() {
    this.rng = () => Math.random();
  }

  // Setter and getter for the model
  setSampleConsensusModel(model) {
    this.sacModel = model;
  }

  getSampleConsensusModel() {
    return this.sacModel;
  }

  // Setter and getter for the distance threshold
  setDistanceThreshold(threshold) {
    this.threshold = threshold;
  }

  getDistanceThreshold() {
    return this.threshold;
  }

  // Setter and getter for max iterations
  setMaxIterations(maxIterations) {
    this.maxIterations = maxIterations;
  }

  getMaxIterations() {
    return this.maxIterations;
  }

  // Setter and getter for probability
  setProbability(probability) {
    this.probability = probability;
  }

  getProbability() {
    return this.probability;
  }

  // Setter and getter for the number of threads (this is not used in this basic JS example)
  setNumberOfThreads(nrThreads = -1) {
    this.threads = nrThreads;
  }

  getNumberOfThreads() {
    return this.threads;
  }

  // Placeholder: The core model computation (needs to be implemented based on the model type)
  computeModel(debugVerbosityLevel = 0) {
    // This function should be implemented in subclasses or using a model-specific logic
    throw new Error("computeModel must be implemented in a subclass.");
  }

  // Refining the model
  refineModel(sigma = 3.0, maxIterations = 1000) {
    console.log('refineModel开始');
    if (!this.sacModel) {
      console.error("[SampleConsensus::refineModel] Critical error: NULL model!");
      return false;
    }

    let inlierDistanceThresholdSqr = this.threshold * this.threshold;
    let errorThreshold = this.threshold;
    let sigmaSqr = sigma * sigma;
    let refineIterations = 0;
    let inlierChanged = false;
    let oscillating = false;

    let prevInliers = this.inliers || [];
    let newInliers = [];
    let inliersSizes = [];
    let newModelCoefficients = this.modelCoefficients || [];

    do {
      // Optimize model coefficients
      newModelCoefficients = this.sacModel.optimizeModelCoefficients(prevInliers, newModelCoefficients);
      inliersSizes.push (prevInliers.length);
      // Select new inliers based on optimized coefficients and threshold
      this.sacModel.selectWithinDistance(newModelCoefficients, errorThreshold, newInliers);

      console.debug(`[refineModel] Number of inliers found (before/after): ${prevInliers.length}/${newInliers.length}, with an error threshold of ${errorThreshold}.`);
      console.info(
        `newModelCoefficients: [${newModelCoefficients[0]}, ${newModelCoefficients[1]}, ${newModelCoefficients[2]}, ${newModelCoefficients[3]}]`
      );
      if (newInliers.length === 0) {
        refineIterations++;
        if (refineIterations >= maxIterations) break;
        continue;
      }

      // Estimate variance and new threshold
      const variance = this.sacModel.computeVariance();
      errorThreshold = Math.sqrt(Math.min(inlierDistanceThresholdSqr, sigmaSqr * variance));

      console.debug(`[refineModel] New estimated error threshold: ${errorThreshold} on iteration ${refineIterations} out of ${maxIterations}`);

      inlierChanged = false;
      [prevInliers, newInliers] = [newInliers, prevInliers]; // Swap previous and new inliers

      if (newInliers.length !== prevInliers.length) {
        if (inliersSizes.length >= 4) {
          if (
            inliersSizes[inliersSizes.length - 1] === inliersSizes[inliersSizes.length - 3] &&
            inliersSizes[inliersSizes.length - 2] === inliersSizes[inliersSizes.length - 4]
          ) {
            oscillating = true;
            break;
          }
        }
        console.log(`inlierChanged = true 1`);
        inlierChanged = true;
        continue;
      }

      // Check inlier set values
      for (let i = 0; i < prevInliers.length; ++i) {
        if (prevInliers[i] !== newInliers[i]) {
          console.log(`inlierChanged = true 2`);
          inlierChanged = true;
          break;
        }
      }
    } while (inlierChanged && ++refineIterations < maxIterations);

    // If no inliers found after refinement
    if (newInliers.length === 0) {
      console.error("[refineModel] Refinement failed: got an empty set of inliers!");
      return false;
    }

    if (oscillating) {
      console.debug("[refineModel] Detected oscillations in model refinement.");
      return true;
    }

    // If no inlier change occurred, refinement successful
    if (!inlierChanged) {
      // this.inliers = newInliers;
      [this.inliers, newInliers] = [newInliers, this.inliers]; // Swap previous and new inliers
      this.modelCoefficients = newModelCoefficients;
      console.info(
        `this.modelCoefficients: [${this.modelCoefficients[0]}, ${this.modelCoefficients[1]}, ${this.modelCoefficients[2]}, ${this.modelCoefficients[3]}]`
      );

      return true;
    }

    return false;
  }

  // Get a random sample of indices
  getRandomSamples(indices, nrSamples) {
    const indicesSubset = new Set();
    while (indicesSubset.size < nrSamples) {
      indicesSubset.add(indices[Math.floor(indices.length * this.rng())]);
    }
    return Array.from(indicesSubset);
  }

  // Get the best model found so far
  getModel() {
    return this.model;
  }

  // Get the inliers of the best model
  getInliers() {
    return this.inliers;
  }

  // Get the model coefficients of the best model
  getModelCoefficients() {
    return this.modelCoefficients;
  }
}
